// src/encounter/l360/l360-activation-panel.tsx
//
// Longevity 360 activation · Signal level: block · nodes A1, A2, A3 (WP7).
//   A1 compose one care plan   goals by track; signed targets apart from proposed; Sign per proposed target.
//   A2 accountable team        role text alone is Pending; Accept is the existing Claim.
//   A3 ready to activate?      shows the Workflows A3 gate result (see l360-a3-section.tsx); no verdict of its own.
//
// Renders under CarePlanReadout, only for a plan with a goal whose sourceNodeId starts with M4-, M5-, F5- or P5-.
// Reads the plan from the same query as the readout (one request, one cache entry). Nothing here writes plan
// data except the Sign and Accept actions.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, cn, StatusChip, TEXT_ROLE_CLASSES, TEXT_TONE_CLASSES } from '@scripsteam/scrips-signal-ds';
import { formatReadoutDate } from '../lib/care-plan-readout-text';
import type { CarePlanView, GoalView } from '../lib/care-plan-view';
import { canAccept, CONTRIBUTORS_UNAVAILABLE, groupGoalsByTrack, isL360Plan, memberPerson, memberStateLabel } from '../lib/l360-activation';
import { isStale412 } from '../services/l360-api';
import { useAuthStore } from '@/auth/auth-store';
import { useCarePlans } from '../services/use-care-plans';
import { activationSnapshotKey, careModelRunPrefix, useCareTeam, useClaimTask, useCreateTeamTasks, useL360TargetsSignedEvent, useSignGoal } from '../services/use-l360-activation';
import type { TeamMemberWire } from '@/types/l360-api';
import { A3Section } from './l360-a3-section';

const SECONDARY = cn(TEXT_ROLE_CLASSES.meta, TEXT_TONE_CLASSES.secondary);

const RESULT_TEXT = { created: 'task created', existing: 'task already exists', failed: 'task not created' } as const;

function SectionTitle({ children }: { children: string }) {
  return <h3 className={cn(TEXT_ROLE_CLASSES['body-emphasis'], TEXT_TONE_CLASSES.primary)}>{children}</h3>;
}

function targetText(goal: GoalView): string {
  const t = goal.target;
  if (t) return `Target ${t.comparator}${t.value}${t.unit ? ` ${t.unit}` : ''}`;
  return goal.targetSet ? 'Target not readable' : 'No numeric target';
}

// ---- A1 ----

function GoalRow({ goal, onSign, busy }: { goal: GoalView; onSign?: () => void; busy: boolean }) {
  return (
    <li data-block="L360Goal" className="flex items-start justify-between gap-3 border-b border-border-subtle px-3 py-2">
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.primary)}>{goal.title}</span>
        <span className={SECONDARY}>
          {targetText(goal)}
          {goal.signoff ? ` · signed by ${goal.signoff.signedBy} on ${formatReadoutDate(goal.signoff.signedAt)}` : ''}
        </span>
      </span>
      {onSign ? (
        <Button variant="secondary" size="sm" onClick={onSign} disabled={busy} aria-label={`Sign target: ${goal.title}`}>
          Sign
        </Button>
      ) : (
        <StatusChip semantic="success" tone="soft">
          Signed
        </StatusChip>
      )}
    </li>
  );
}

function GoalsSection({ patientId, plan, onSigned }: { patientId: string; plan: CarePlanView; onSigned: () => void }) {
  const sign = useSignGoal(patientId, plan.carePlanId);
  const groups = groupGoalsByTrack(plan.goals);
  const revision = plan.revision ?? null;
  return (
    <div data-node="A1" aria-label="A1 compose one care plan" role="group" className="flex flex-col gap-2">
      <SectionTitle>Targets by track</SectionTitle>
      {sign.isError && (
        <p role="alert" className={cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.primary)}>
          {isStale412(sign.error) ? 'Plan changed, review again' : 'The target could not be signed.'}
        </p>
      )}
      {revision === null && <p className={SECONDARY}>Plan revision not available, so targets cannot be signed yet.</p>}
      {groups.map((g) => (
        <div key={g.track} data-track={g.track} className="flex flex-col">
          <span className={cn(TEXT_ROLE_CLASSES['meta-strong'], TEXT_TONE_CLASSES.secondary)}>{g.label}</span>
          <ul aria-label={`${g.label} signed targets`} className="flex flex-col">
            <li className={cn('px-3 pt-1', SECONDARY)}>Signed targets</li>
            {g.signed.length === 0 && <li className={cn('px-3 py-1', SECONDARY)}>None signed</li>}
            {g.signed.map((goal) => (
              <GoalRow key={goal.id} goal={goal} busy={false} />
            ))}
          </ul>
          <ul aria-label={`${g.label} proposed targets`} className="flex flex-col">
            <li className={cn('px-3 pt-1', SECONDARY)}>Proposed</li>
            {g.proposed.length === 0 && <li className={cn('px-3 py-1', SECONDARY)}>None proposed</li>}
            {g.proposed.map((goal) => (
              <GoalRow
                key={goal.id}
                goal={goal}
                busy={sign.isPending || revision === null}
                onSign={() => revision !== null && sign.mutate({ goalId: goal.id, expectedRevision: revision }, { onSuccess: onSigned })}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ---- A2 ----

function MemberRow({ member, onAccept, busy }: { member: TeamMemberWire; onAccept: () => void; busy: boolean }) {
  const currentUserId = useAuthStore((s) => s.user?.userId);
  const accepted = member.accepted;
  return (
    <li data-block="L360Member" className="flex items-center justify-between gap-3 border-b border-border-subtle px-3 py-2">
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={cn(TEXT_ROLE_CLASSES.body, TEXT_TONE_CLASSES.primary)}>{member.role}</span>
        <span className={SECONDARY}>{memberPerson(member, currentUserId)}</span>
      </span>
      <StatusChip semantic={accepted ? 'success' : 'warning'} tone="soft">
        {memberStateLabel(member)}
      </StatusChip>
      {canAccept(member) && (
        <Button variant="secondary" size="sm" onClick={onAccept} disabled={busy} aria-label={`Accept task: ${member.role}`}>
          Accept
        </Button>
      )}
    </li>
  );
}

function TeamSection({ patientId, plan }: { patientId: string; plan: CarePlanView }) {
  const team = useCareTeam(patientId, plan.carePlanId);
  const create = useCreateTeamTasks(patientId, plan.carePlanId);
  const claim = useClaimTask(patientId, plan.carePlanId);
  const members = team.data?.members ?? [];
  const failedRoles = (create.data ?? []).filter((o) => o.result === 'failed').map((o) => o.role);
  return (
    <div data-node="A2" aria-label="A2 accountable team" role="group" className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <SectionTitle>Accountable team</SectionTitle>
        <Button variant="secondary" size="sm" onClick={() => create.mutate()} disabled={create.isPending}>
          Create team tasks
        </Button>
      </div>
      {plan.contributorsAvailable === false && <p className={SECONDARY}>{CONTRIBUTORS_UNAVAILABLE}</p>}
      {team.isError && <p className={SECONDARY}>The team could not be loaded.</p>}
      {create.isError && <p role="alert" className={SECONDARY}>The team tasks could not be created.</p>}
      {create.data && (
        <p className={SECONDARY}>
          {create.data.map((o) => `${o.role}: ${RESULT_TEXT[o.result]}`).join(' · ')}
        </p>
      )}
      {failedRoles.length > 0 && (
        <p role="alert" className={SECONDARY}>{`Tasks could not be created for: ${failedRoles.join(', ')}.`}</p>
      )}
      {claim.isError && <p role="alert" className={SECONDARY}>The task could not be accepted. Reload and try again.</p>}
      {team.isSuccess && members.length === 0 && <p className={SECONDARY}>No team members or tasks yet.</p>}
      <ul aria-label="Accountable team members" className="flex flex-col">
        {members.map((m, i) => (
          <MemberRow
            key={m.taskId ?? `${m.source}-${m.role}-${m.practitionerId ?? i}`}
            member={m}
            busy={claim.isPending}
            onAccept={() => m.taskId && typeof m.taskVersion === 'number' && claim.mutate({ taskId: m.taskId, version: m.taskVersion })}
          />
        ))}
      </ul>
    </div>
  );
}

function PlanActivation({ patientId, plan }: { patientId: string; plan: CarePlanView }) {
  const qc = useQueryClient();
  useL360TargetsSignedEvent(patientId, plan);
  // A Sign that succeeds changes the Patient plan revision. A3 must then take a new snapshot, so the approver's
  // own sign is not a 409. Both reads are refetched (not served from cache) before the counter changes the key.
  const [signCount, setSignCount] = useState(0);
  const onSigned = () => {
    void qc.invalidateQueries({ queryKey: activationSnapshotKey(patientId, plan.carePlanId) });
    void qc.invalidateQueries({ queryKey: careModelRunPrefix });
    setSignCount((n) => n + 1);
  };
  return (
    <div data-block="L360Plan" className="flex flex-col gap-4 rounded-lg border border-border-card bg-surface-card p-3">
      <GoalsSection patientId={patientId} plan={plan} onSigned={onSigned} />
      <TeamSection patientId={patientId} plan={plan} />
      <A3Section patientId={patientId} carePlanId={plan.carePlanId} definitionId={plan.definitionId} resnapshot={signCount} />
    </div>
  );
}

/** The Longevity 360 activation section for one patient. Renders nothing unless a plan has an L360 goal. */
export function L360ActivationPanel({ patientId, className }: { patientId: string | null | undefined; className?: string }) {
  const plans = useCarePlans(patientId);
  const l360 = (plans.data ?? []).filter(isL360Plan);
  if (!patientId || l360.length === 0) return null;
  return (
    <section aria-label="Longevity 360 activation" data-nodes="A1 A2 A3" className={cn('flex flex-col gap-2', className)}>
      <h2 className={cn(TEXT_ROLE_CLASSES['body-emphasis'], TEXT_TONE_CLASSES.primary)}>Longevity 360 activation</h2>
      {l360.map((plan) => (
        <PlanActivation key={plan.carePlanId} patientId={patientId} plan={plan} />
      ))}
    </section>
  );
}
